interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
  itemLabel?: string;
  className?: string;
}

function buildPages(current: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  if (current <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", totalPages];
  }
  if (current >= totalPages - 3) {
    return [1, "ellipsis", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "ellipsis", current - 1, current, current + 1, "ellipsis", totalPages];
}

export default function Pagination({
  page,
  totalPages,
  total,
  pageSize,
  onChange,
  itemLabel = "条",
  className = "",
}: PaginationProps) {
  if (total === 0) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const pages = buildPages(page, totalPages);

  const navButtonClass =
    "flex h-8 min-w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 bg-background-50 px-2 text-xs text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <div className={`mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row ${className}`}>
      <p className="text-xs text-foreground-500">
        显示第 {start}-{end} {itemLabel}，共 {total} {itemLabel}
      </p>
      {totalPages > 1 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onChange(page - 1)}
            aria-label="上一页"
            className={navButtonClass}
          >
            <i className="ri-arrow-left-s-line text-base" />
          </button>
          {pages.map((item, index) =>
            item === "ellipsis" ? (
              <span
                key={`ellipsis-${index}`}
                className="flex h-8 min-w-8 items-center justify-center px-1 text-xs text-foreground-400"
              >
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => onChange(item)}
                className={`flex h-8 min-w-8 cursor-pointer items-center justify-center rounded-md px-2 text-xs transition-colors ${
                  item === page
                    ? "bg-primary-500 font-medium text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item}
              </button>
            ),
          )}
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onChange(page + 1)}
            aria-label="下一页"
            className={navButtonClass}
          >
            <i className="ri-arrow-right-s-line text-base" />
          </button>
        </div>
      )}
    </div>
  );
}